'use client'

import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Icon,
  IconButton,
  Input,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react'
import { createListCollection } from '@chakra-ui/react'
import { useDisclosure } from '@chakra-ui/react'
import { forwardRef, useEffect, useMemo, useState } from 'react'
import {
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaFile,
  FaRegEye,
  FaSearch,
} from 'react-icons/fa'
import type { Attachment } from '@/utils/types'

import type {
  IRCompanyConfig,
  IRDocument,
  IRThemeConfig,
  InvestorRelationsConfigContextType,
} from '@/utils/investorRelations'
import {
  DocumentYearFilter,
  getAvailableDocumentYears,
  getPublishedYear,
} from './DocumentYearFilter'
import InvestorRelationsDocumentViewer from '@/components/InvestorRelations/InvestorRelationsDocumentViewer'

interface DocumentsSectionProps {
  documents: IRDocument[]
  isLoading: boolean
  isPreview?: boolean
  organisationId?: string
  title: string
  backgroundColor: boolean
  sectionId: string
  deepLinkedDocumentId?: string | null
  onDeepLinkedDocumentClose?: (sectionId: string) => void
  onDocumentOpen?: (sectionId: string, documentId: string) => void
  useContextFunction: () => InvestorRelationsConfigContextType
}

const ITEMS_PER_PAGE = 5

function formatDateShort(date: string | number | Date): string {
  const d = new Date(date)
  const day = d.getDate()
  const month = d.toLocaleString('default', { month: 'short' })
  const year = d.getFullYear()
  return `${day} ${month} ${year}`
}

function sanitizeSearchInput(input: string): string {
  const MAX_SEARCH_LENGTH = 20
  return input.trim().slice(0, MAX_SEARCH_LENGTH).replace(/[<>]/g, '') // Remove potential HTML tags
}

export const DocumentsSection = forwardRef<HTMLElement, DocumentsSectionProps>(
  function DocumentsSection(
    {
      documents,
      isLoading,
      title,
      isPreview = false,
      organisationId = undefined,
      backgroundColor,
      sectionId,
      deepLinkedDocumentId = null,
      onDeepLinkedDocumentClose,
      onDocumentOpen,
      useContextFunction,
    },
    ref
  ) {
    const [currentPage, setCurrentPage] = useState(1)
    const [searchQuery, setSearchQuery] = useState('')
    const [selectedType, setSelectedType] = useState<string>('all')
    const [selectedYear, setSelectedYear] = useState<number | null>(null)
    const [selectedAttachmentForPreview, setSelectedAttachmentForPreview] =
      useState<Attachment | null>(null)
    const attachmentPreviewer = useDisclosure()

    const { config, loading, error } =
      useContextFunction() as InvestorRelationsConfigContextType

    const themeConfig = ((config?.themeConfig as IRThemeConfig) ||
      {}) as IRThemeConfig
    const primaryColor = themeConfig.primaryColor
    const textColor = themeConfig.textColor
    const fontFamily = themeConfig.fontStyle

    // Map sectionId to API section name
    const getSectionName = (
      sectionId: string
    ): 'regulatory' | 'reports' | 'documents' => {
      const sectionMap: Record<string, 'regulatory' | 'reports' | 'documents'> =
        {
          'regulatory-news': 'regulatory',
          results: 'reports',
          documents: 'documents',
        }
      return sectionMap[sectionId]
    }

    const sectionName = getSectionName(sectionId)

    const allAttachments: IRDocument[] = useMemo(() => {
      if (!documents) return []
      return [...documents].sort((a, b) => {
        const aIsNotPublished = a.publishedDate === '[Not published]'
        const bIsNotPublished = b.publishedDate === '[Not published]'

        // Keep not published documents on top
        if (aIsNotPublished && !bIsNotPublished) return -1
        if (!aIsNotPublished && bIsNotPublished) return 1

        // If both are not published, sort by createdAt (newest first)
        if (aIsNotPublished && bIsNotPublished) {
          const dateA = new Date(a.createdAt).getTime()
          const dateB = new Date(b.createdAt).getTime()
          return dateB - dateA
        }

        // If both are published, sort by publishedDate (newest first)
        const dateA = new Date(a.publishedDate).getTime()
        const dateB = new Date(b.publishedDate).getTime()
        return dateB - dateA
      })
    }, [documents])

    const availableYears = useMemo(
      () => getAvailableDocumentYears(allAttachments),
      [allAttachments]
    )
    const activeYear = availableYears.includes(selectedYear ?? Number.NaN)
      ? selectedYear
      : availableYears[0] ?? null

    const filteredAttachments = useMemo(() => {
      let filtered = allAttachments

      if (availableYears.length > 1 && activeYear !== null) {
        filtered = filtered.filter(
          (attachment) =>
            getPublishedYear(attachment.publishedDate) === activeYear
        )
      }

      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        filtered = filtered.filter(
          (att) =>
            att.fileName.toLowerCase().includes(query) ||
            att.labels?.some((label) => label.toLowerCase().includes(query))
        )
      }

      if (selectedType !== 'all') {
        filtered = filtered.filter((att) =>
          att.labels?.some((label) =>
            label.toLowerCase().includes(selectedType.toLowerCase())
          )
        )
      }

      return filtered
    }, [
      activeYear,
      allAttachments,
      availableYears.length,
      searchQuery,
      selectedType,
    ])

    const paginatedAttachments = useMemo(() => {
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE
      const endIndex = startIndex + ITEMS_PER_PAGE
      return filteredAttachments.slice(startIndex, endIndex)
    }, [filteredAttachments, currentPage])

    const totalPages = Math.ceil(filteredAttachments.length / ITEMS_PER_PAGE)

    const uniqueLabels = useMemo(() => {
      const labels = new Set<string>(['all'])
      for (const att of allAttachments) {
        if (att.labels && att.labels.length > 0) {
          for (const label of att.labels) {
            labels.add(label)
          }
        }
      }
      return Array.from(labels).sort()
    }, [allAttachments])

    const typeOptions = useMemo(() => {
      return createListCollection({
        items: [
          { value: 'all', label: 'All Labels' },
          ...uniqueLabels
            .filter((label) => label !== 'all')
            .map((label) => ({
              value: label,
              label: label,
            })),
        ],
      })
    }, [uniqueLabels])

    const handleView = (attachment: Attachment) => {
      setSelectedAttachmentForPreview(attachment)
      attachmentPreviewer.onOpen()
      onDocumentOpen?.(sectionId, attachment.attachmentId)
    }

    useEffect(() => {
      if (!deepLinkedDocumentId || attachmentPreviewer.open) return

      const attachment = allAttachments.find(
        (item) => item.attachmentId === deepLinkedDocumentId
      )
      if (!attachment) return

      setSelectedAttachmentForPreview(attachment as unknown as Attachment)
      attachmentPreviewer.onOpen()
    }, [
      allAttachments,
      attachmentPreviewer,
      attachmentPreviewer.open,
      deepLinkedDocumentId,
    ])

    const handlePageChange = (page: number) => {
      setCurrentPage(page)
    }

    return (
      <>
        <Box
          ref={ref}
          as="section"
          id={sectionId}
          bg={backgroundColor ? `${primaryColor}1A` : 'white'}
          minH="100vh"
          display="flex"
          alignItems="center"
          py={{ base: 10, md: 16 }}
        >
          <Container maxW="6xl">
            <Stack gap={{ base: 6, md: 8 }}>
              <Heading
                as="h2"
                size={{ base: 'lg', md: '5xl' }}
                color={primaryColor}
                fontWeight="bold"
                mb={3}
                textAlign="center"
                fontFamily={fontFamily}
              >
                {title}
              </Heading>

              <DocumentYearFilter
                years={availableYears}
                selectedYear={activeYear}
                onYearChange={(year) => {
                  setSelectedYear(year)
                  setCurrentPage(1)
                }}
                primaryColor={primaryColor}
                fontFamily={fontFamily}
                sectionTitle={title}
              />

              <HStack gap={4} flexWrap="wrap">
                <Box
                  position="relative"
                  maxW={{ base: 'full', md: '300px' }}
                  minW={0}
                  w={{ base: 'full', md: '300px' }}
                >
                  <Icon
                    as={FaSearch}
                    position="absolute"
                    left="8px"
                    top="50%"
                    transform="translateY(-50%)"
                    color="gray.400"
                    boxSize="12px"
                  />
                  <Input
                    placeholder="Search"
                    value={searchQuery}
                    onChange={(event) => {
                      setSearchQuery(sanitizeSearchInput(event.target.value))
                      setCurrentPage(1)
                    }}
                    bg="white"
                    borderColor="gray.300"
                    borderRadius="8px"
                    fontSize="10px"
                    h="28px"
                    lineHeight="1"
                    pl="25px"
                    pr="6px"
                    type="search"
                  />
                </Box>
                <Select.Root
                  collection={typeOptions}
                  value={[selectedType]}
                  onValueChange={(details) => {
                    setSelectedType(details.value[0])
                    setCurrentPage(1)
                  }}
                  w={{ base: 'full', md: '200px' }}
                >
                  <Select.HiddenSelect />
                  <Select.Control>
                    <Select.Trigger bg="white" borderColor="gray.300">
                      <Select.ValueText>
                        {selectedType === 'all' ? 'All Labels' : selectedType}
                      </Select.ValueText>
                    </Select.Trigger>
                    <Select.IndicatorGroup>
                      <Select.Indicator />
                    </Select.IndicatorGroup>
                  </Select.Control>
                  <Select.Positioner>
                    <Select.Content>
                      {typeOptions.items.map((item) => (
                        <Select.Item key={item.value} item={item}>
                          <Select.ItemText>{item.label}</Select.ItemText>
                          <Select.ItemIndicator />
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select.Positioner>
                </Select.Root>
              </HStack>

              {isLoading ? (
                <Text color="gray.600" py={8} fontFamily={fontFamily}>
                  Loading documents...
                </Text>
              ) : paginatedAttachments.length === 0 ? (
                <Box
                  bg={backgroundColor ? 'gray.50' : 'white'}
                  borderRadius="lg"
                  boxShadow="md"
                  p={{ base: 8, md: 12 }}
                  textAlign="center"
                >
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    mb={4}
                    opacity={0.5}
                    color="gray.700"
                  >
                    <Icon as={FaFile} boxSize={6} color="gray.400" />
                  </Box>

                  <Heading
                    as="h3"
                    fontSize="xl"
                    fontWeight="semibold"
                    mb={2}
                    color="gray.800"
                    fontFamily={fontFamily}
                  >
                    {title} coming soon
                  </Heading>

                  {sectionId === 'regulatory-news' && (
                    <Text color="gray.700" fontFamily={fontFamily}>
                      Check back soon for our latest updates and company
                      announcements.
                    </Text>
                  )}
                  {sectionId === 'results' && (
                    <Text color="gray.700" fontFamily={fontFamily}>
                      Check back soon for our latest financial reports.
                    </Text>
                  )}
                  {sectionId === 'documents' && (
                    <Text color="gray.700" fontFamily={fontFamily}>
                      Check back soon for our latest corporate documents.
                    </Text>
                  )}
                </Box>
              ) : (
                <Stack gap={4} overflowY="auto">
                  {paginatedAttachments.map((attachment) => {
                    return (
                      <Box
                        key={attachment.attachmentId}
                        borderWidth="1px"
                        borderRadius="lg"
                        bg="white"
                        p={4}
                        boxShadow="sm"
                        _hover={{ shadow: 'md', transform: 'translateY(-2px)' }}
                        onClick={() =>
                          handleView(attachment as unknown as Attachment)
                        }
                        transition="all 0.2s"
                      >
                        <Flex
                          direction={{ base: 'column', md: 'row' }}
                          gap={4}
                          align={{ base: 'flex-start', md: 'center' }}
                          justify="space-between"
                        >
                          <Box flex="1" minW={0} w="full">
                            <HStack gap={3} mb={2} flexWrap="wrap">
                              <Text
                                fontSize="sm"
                                color="gray.600"
                                fontFamily={fontFamily}
                              >
                                {attachment.publishedDate === '[Not published]'
                                  ? 'Not published'
                                  : formatDateShort(attachment.publishedDate)}
                              </Text>
                              {attachment.labels?.map((label) => (
                                <Box
                                  key={label}
                                  px={2}
                                  py={1}
                                  borderRadius="md"
                                  bg={`${primaryColor}1A`}
                                  color={primaryColor}
                                  fontSize="xs"
                                  fontWeight="medium"
                                >
                                  {label}
                                </Box>
                              ))}
                            </HStack>
                            <Heading
                              as="h3"
                              size="md"
                              color={textColor}
                              fontWeight="semibold"
                              fontFamily={fontFamily}
                              maxW="full"
                              overflowWrap="anywhere"
                              wordBreak="break-word"
                              lineHeight="1.35"
                            >
                              {attachment.fileName}
                            </Heading>
                          </Box>
                        </Flex>
                      </Box>
                    )
                  })}
                </Stack>
              )}

              {totalPages > 1 && (
                <Box>
                  <Flex
                    align="center"
                    justify="center"
                    gap={2}
                    borderTop="1px solid"
                    borderColor="gray.200"
                    pt={6}
                  >
                    <IconButton
                      aria-label="Previous page"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      variant="ghost"
                      color={primaryColor}
                    >
                      <FaChevronLeft />
                    </IconButton>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                      (page) => (
                        <Button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          variant={page === currentPage ? 'solid' : 'ghost'}
                          color={page === currentPage ? 'white' : primaryColor}
                          aria-current={
                            page === currentPage ? 'page' : undefined
                          }
                          size="sm"
                          minW="40px"
                        >
                          {page}
                        </Button>
                      )
                    )}
                    <IconButton
                      aria-label="Next page"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      variant="ghost"
                      color={primaryColor}
                    >
                      <FaChevronRight />
                    </IconButton>
                  </Flex>
                </Box>
              )}
            </Stack>
          </Container>
        </Box>
        <InvestorRelationsDocumentViewer
          attachment={selectedAttachmentForPreview}
          isOpen={attachmentPreviewer.open}
          anonymousMode={true}
          companySlug={config?.companySlugPublished || ''}
          section={sectionName}
          isPreview={isPreview}
          organisationId={organisationId}
          companyLogoUrl={config?.companyConfig?.logoUrl}
          onClose={() => {
            const closedAttachmentId =
              selectedAttachmentForPreview?.attachmentId
            attachmentPreviewer.onClose()
            setSelectedAttachmentForPreview(null)
            if (closedAttachmentId === deepLinkedDocumentId) {
              onDeepLinkedDocumentClose?.(sectionId)
            }
          }}
        />
      </>
    )
  }
)
